import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import Button from "./ui/button";
import ScenarioMiniCard from "./ScenarioMiniCard";
import './PendingNodesPanel.css';

// === FONCTIONS UTILITAIRES ===
const calculateUrgencyScore = (node) => {
  let score = 0;
  const now = new Date();
  const nodeDate = new Date(node.createdAt);
  const daysDiff = Math.floor((now - nodeDate) / (1000 * 60 * 60 * 24));

  // === SCORING BY AGE ===
  if (daysDiff > 7) score += 30;      // 1 week = critical
  else if (daysDiff > 3) score += 20; // 3 days = urgent
  else if (daysDiff > 1) score += 10; // 1 day = attention

  // === SCORING BY METADATA ===
  if (node.metadata?.priority === "urgent") score += 40;
  if (node.metadata?.priority === "high") score += 25;
  if (node.metadata?.hasErrors) score += 35;
  if (node.metadata?.isBlocking) score += 50;

  // === SCORING BY CONTENT ===
  const desc = node.description?.toLowerCase() || '';
  if (desc.includes('erreur') || desc.includes('bug')) score += 25;
  if (desc.includes('urgent') || desc.includes('critique')) score += 30;
  if (desc.includes('bloquant') || desc.includes('production')) score += 45;
  if (desc.includes('sécurité') || desc.includes('security')) score += 35;

  // === SCORING BY TYPE ===
  if (node.type === "Test" && desc.includes('échec')) score += 20;
  if (node.type === "Bug" || node.type === "Hotfix") score += 40;

  // === SCORING BY SIZE/COMPLEXITY ===
  // 1. D'abord vérifier les scénarios dans metadata
  let scenarios = [];
  if (Array.isArray(node.metadata?.scenarios) && node.metadata.scenarios.length > 0) {
    scenarios = node.metadata.scenarios;
  } 
  // 🚀 NOUVEAU : Détecter les tests générés avec steps
  else if (node.type === "Test" && (node.steps || node.metadata?.steps) && Array.isArray(node.steps || node.metadata?.steps)) {
    scenarios = [{ steps: node.steps || node.metadata?.steps }]; // Simuler un scénario
  }
  // 2. Sinon essayer le parsing de la description
  else {
    scenarios = node.scenarios || parseDescriptionForScenarios(node.description);
  }
  
  if (scenarios.length > 5) score += 15; // Many scenarios = complex

  return Math.min(score, 100); // Cap at 100
};

// Simple function to get urgency level with translations
const getUrgencyLevel = (score, t) => {
  if (score >= 60) return { level: 'critical', label: `🔥 ${t('pendingNodesPanel.urgency.critical')}`, color: '#dc2626' };
  if (score >= 40) return { level: 'high', label: `⚠️ ${t('pendingNodesPanel.urgency.high')}`, color: '#ea580c' };
  if (score >= 20) return { level: 'medium', label: `🟡 ${t('pendingNodesPanel.urgency.medium')}`, color: '#d97706' };
  return { level: 'low', label: `✅ ${t('pendingNodesPanel.urgency.low')}`, color: '#16a34a' };
};

const needsAttention = (node) => {
  return calculateUrgencyScore(node) >= 20; // Seuil ajustable
};

const isTest = (node) => {
  // 👈 NOUVEAU : Si c'est explicitement marqué comme un vrai nœud, ce n'est pas un test
  if (node.isActualNode === true) {
    return false;
  }
  
  // Check multiple criteria to identify a test
  return (
    node.type === "Test" ||
    node.metadata?.scenarioType ||
    node.metadata?.isTest ||
    (node.metadata?.steps && Array.isArray(node.metadata.steps)) ||
    (node.name && node.name.toLowerCase().includes('test')) ||
    (node.name && node.name.toLowerCase().includes('scénario'))
  );
};

// Ligne 57-59, remplace par :
const isNode = (node) => {
  // Debug removed to avoid infinite loop
  return !isTest(node);
};

const calculateDaysOld = (node) => {
  const now = new Date();
  const nodeDate = new Date(node.createdAt);
  return Math.floor((now - nodeDate) / (1000 * 60 * 60 * 24));
};

const getRecentlyProcessed = () => {
  // Simulate recently processed items
  return [];
};

// === FONCTION PRINCIPALE DE PARSING ===
const parseDescriptionForScenarios = (description) => {
  if (!description) return [];

  const scenarios = [];

  try {
    // console.log('🔍 Parsing description:', description.substring(0, 200) + '...'); // Verbose log commented

    // === 1. PARSER LE JSON DANS LES BLOCS DE CODE ===
    const jsonBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/gi;
    let jsonMatch;

    while ((jsonMatch = jsonBlockRegex.exec(description)) !== null) {
      try {
        const jsonContent = jsonMatch[1].trim();
        const parsedJson = JSON.parse(jsonContent);

        if (Array.isArray(parsedJson)) {
          // console.log('✅ JSON Array found:', parsedJson.length, 'scenarios'); // Verbose log commented
          parsedJson.forEach((scenario) => {
            scenarios.push({
              title: scenario.title || t('pendingNodesPanel.scenarios.untitledScenario'),
              type: scenario.type || "Fonctionnel",
              positive: scenario.positive !== undefined ? scenario.positive : true,
              steps: scenario.steps || [],
              testType: scenario.positive ? "positive" : "negative",
              category: "functional",
            });
          });
        }
      } catch (jsonError) {
        console.log("⚠️ Erreur parsing JSON:", jsonError.message);
      }
    }

    // === 2. PARSER LE FORMAT MARKDOWN ===
    if (scenarios.length === 0) {
      // console.log('🔄 No JSON, attempting Markdown parsing...'); // Verbose log commented

      const scenarioRegex = /-\s*\*\*Scénario\s+\d+:\s*([^*]+)\*\*/gi;
      let scenarioMatch;

      while ((scenarioMatch = scenarioRegex.exec(description)) !== null) {
        const scenarioTitle = scenarioMatch[1].trim();
        const scenarioStart = scenarioMatch.index;

        const nextScenarioStart = description.indexOf("- **Scénario", scenarioStart + 1);
        const scenarioBlock =
          nextScenarioStart === -1
            ? description.substring(scenarioStart)
            : description.substring(scenarioStart, nextScenarioStart);

        const stepRegex = /-\s*\*\*(Given|When|Then|And|But)\*\*\s*([^\n\r-]+)/gi;
        const steps = [];
        let stepMatch;

        while ((stepMatch = stepRegex.exec(scenarioBlock)) !== null) {
          const stepType = stepMatch[1];
          const stepText = stepMatch[2].trim();

          steps.push({
            type: stepType,
            text: stepText,
          });
        }

        const lowerTitle = scenarioTitle.toLowerCase();
        const isNegative = lowerTitle.includes('erreur') ||
          lowerTitle.includes('échec') ||
          lowerTitle.includes('invalide') ||
          lowerTitle.includes('refus');

        if (steps.length > 0) {
          scenarios.push({
            title: scenarioTitle,
            type: "Fonctionnel",
            positive: !isNegative,
            steps: steps,
            testType: isNegative ? "negative" : "positive",
            category: "functional",
          });

          console.log(`✅ Scénario Markdown créé: "${scenarioTitle}" (${steps.length} étapes)`);
        }
      }
    }

    // === 3. FALLBACK : PARSER TEXTE SIMPLE ===
    if (scenarios.length === 0) {
      // console.log('🔄 Fallback: plain text parsing...'); // Verbose log commented

      const gherkinMatches = description.match(/(Given|When|Then|And|But)[^a-z\n]([^\n]+)/gi);
      if (gherkinMatches && gherkinMatches.length >= 2) {
        const steps = gherkinMatches.map(match => {
          const [, type, text] = match.match(/(Given|When|Then|And|But)\s*(.+)/i);
          return { type, text: text.trim() };
        });

        scenarios.push({
          title: 'Scénario extrait du texte', // TODO: This should be translated if moved to component level
          type: 'Fonctionnel',
          positive: true,
          steps: steps,
          testType: 'positive',
          category: 'functional'
        });

        console.log('✅ Scénario fallback créé avec', steps.length, 'étapes');
      }
    }

    //console.log(`🎯 Total scénarios extraits: ${scenarios.length}`);
    return scenarios;
  } catch (error) {
    console.error("❌ Erreur lors du parsing:", error);
    return [];
  }
};

// === COMPOSANTS UTILITAIRES ===
const StatsBadge = ({ count, label, variant = "default" }) => {
  const variantClasses = {
    urgent: "stats-badge-urgent",
    tests: "stats-badge-tests",
    nodes: "stats-badge-nodes",
    default: "stats-badge-default",
  };

  return (
    <div className={`stats-badge ${variantClasses[variant]}`}>
      <span className="stats-count">{count}</span>
      <span className="stats-label">{label}</span>
    </div>
  );
};

const FilterButton = ({ active, onClick, count, variant = "default", children }) => {
  const variantClasses = {
    urgent: "filter-urgent",
    tests: "filter-tests",
    nodes: "filter-nodes",
    default: "filter-default",
  };

  return (
    <button
      className={`filter-btn ${active ? "active" : ""} ${variantClasses[variant]}`}
      onClick={onClick}
    >
      {children}
      {count > 0 && <span className="filter-count">({count})</span>}
    </button>
  );
};

// === COMPOSANTS DE VUE ===
const DetailedSingleColumn = ({ title, items, handlers, variant, emptyMessage }) => {
  return (
    <div className={`detailed-single-column ${variant}`}>
      <div className="detailed-column-header">
        <h3 className="detailed-title">{title}</h3>
        <div className="detailed-stats">
          <span className="detailed-count">{items.length}</span>
          <span className="detailed-label">élément(s)</span>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="detailed-empty-state">
          <div className="empty-state-icon">
            {variant === 'urgent' && '🔥'}
            {variant === 'tests' && '🧪'}
            {variant === 'nodes' && '🔷'}
          </div>
          <h4>Rien à afficher</h4>
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <div className="detailed-items-container">
          {items.map(item => (
            <DetailedKanbanCard
              key={item.id}
              item={item}
              handlers={handlers}
              variant={variant}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DetailedKanbanCard = ({ item, handlers, variant }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(true);
  const [showAllScenarios, setShowAllScenarios] = useState(false);

  return (
    <div className={`detailed-kanban-card ${variant}`}>
      {/* En-tête élargie */}
      <div className="detailed-card-header">
        <div className="detailed-card-main-info">
          <div className="detailed-card-title">
            <span className="detailed-card-icon">{isTest(item) ? "🧪" : "🔷"}</span>
            <div className="title-and-meta">
              <span className="detailed-card-name">{item.name}</span>
              <div className="detailed-card-meta">
                <span className="detailed-author">👤 {item.author}</span>
                <span className="detailed-date">📅 {new Date(item.createdAt).toLocaleDateString()}</span>
                <span className="detailed-age">⏰ {calculateDaysOld(item)} jours</span>
              </div>
            </div>
          </div>
        </div>

        {/* Badge d'urgence plus visible */}
        <div className="detailed-urgency-section">
          {(() => {
            const score = calculateUrgencyScore(item);
            const urgency = getUrgencyLevel(score, t);

            return (
              <div className="detailed-urgency-info">
                <div
                  className="detailed-urgency-badge"
                  style={{ backgroundColor: urgency.color }}
                  title={t('titles.urgencyScore', { score })}
                >
                  {urgency.label}
                </div>
                <div className="urgency-score">
                  Score: {score}/100
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Contenu détaillé */}
      <div className="detailed-card-content">
        {(() => {
          // 1. Check if we have scenarios in metadata
          if (Array.isArray(item.metadata?.scenarios) && item.metadata.scenarios.length > 0) {
            const scenarios = item.metadata.scenarios;
            const displayedScenarios = showAllScenarios ? scenarios : scenarios.slice(0, 5);

            return (
              <div className="detailed-scenarios-section">
                <div className="detailed-scenarios-header">
                  <span className="detailed-scenarios-count">📋 {scenarios.length} scénario(s)</span>
                  {scenarios.length > 5 && (
                    <button
                      className="detailed-scenarios-toggle"
                      onClick={() => setShowAllScenarios(!showAllScenarios)}
                    >
                      {showAllScenarios ? '📖 Voir moins' : '📖 Voir tout'}
                    </button>
                  )}
                </div>

                <div className="detailed-scenarios-list">
                  {displayedScenarios.map((sc, i) => (
                    <div key={sc.id || sc.uniqueKey || `scenario-${i}-${Date.now()}`} className="detailed-scenario-item">
                      <ScenarioMiniCard scenario={sc} />
                    </div>
                  ))}
                </div>

                {!showAllScenarios && scenarios.length > 5 && (
                  <div className="detailed-scenarios-more">
                    <button
                      className="detailed-show-more-btn"
                      onClick={() => setShowAllScenarios(true)}
                    >
                      📋 {t('placeholders.showOtherScenarios', { count: scenarios.length - 5 })}
                    </button>
                  </div>
                )}
              </div>
            );
          }

          // 🚀 NOUVEAU : Détecter les tests générés avec steps mais sans scenarios
          if (isTest(item) && (item.steps || item.metadata?.steps) && Array.isArray(item.steps || item.metadata?.steps) && (item.steps || item.metadata?.steps).length > 0) {
            const steps = item.steps || item.metadata?.steps;
            
            // Créer un scénario virtuel à partir des propriétés du test
            const virtualScenario = {
              id: item.id || `virtual-${Date.now()}`,
              title: item.name || item.title || 'Test généré',
              type: item.metadata?.scenarioType || item.category || 'acceptance',
              positive: item.positive !== false,
              steps: steps,
              targetNode: item.targetNode || item.metadata?.targetNode,
              description: item.description
            };

            return (
              <div className="detailed-scenarios-section">
                <div className="detailed-scenarios-header">
                  <span className="detailed-scenarios-count">🧪 Test généré ({steps.length} étapes)</span>
                </div>

                <div className="detailed-scenarios-list">
                  <div className="detailed-scenario-item">
                    <ScenarioMiniCard scenario={virtualScenario} />
                  </div>
                </div>
              </div>
            );
          }

          // 2. Parse description to extract scenarios
          const parsedScenarios = item.scenarios || item.metadata?.scenarios || parseDescriptionForScenarios(item.description);
          if (parsedScenarios.length > 0) {
            const displayedScenarios = showAllScenarios ? parsedScenarios : parsedScenarios.slice(0, 5);

            return (
              <div className="detailed-scenarios-section">
                <div className="detailed-scenarios-header">
                  <span className="detailed-scenarios-count">🔍 {parsedScenarios.length} {t('placeholders.detectedScenarios')}</span>
                  {parsedScenarios.length > 5 && (
                    <button
                      className="detailed-scenarios-toggle"
                      onClick={() => setShowAllScenarios(!showAllScenarios)}
                    >
                      {showAllScenarios ? t('placeholders.seeLess') : t('placeholders.seeAll')}
                    </button>
                  )}
                </div>

                <div className="detailed-scenarios-list">
                  {displayedScenarios.map((sc, i) => (
                    <div key={sc.id || sc.uniqueKey || `scenario-b-${i}-${Date.now()}`} className="detailed-scenario-item">
                      <ScenarioMiniCard scenario={sc} />
                    </div>
                  ))}
                </div>

                {!showAllScenarios && parsedScenarios.length > 5 && (
                  <div className="detailed-scenarios-more">
                    <button
                      className="detailed-show-more-btn"
                      onClick={() => setShowAllScenarios(true)}
                    >
                      📋 {t('placeholders.showOtherScenarios', { count: parsedScenarios.length - 5 })}
                    </button>
                  </div>
                )}
              </div>
            );
          }

          // 3. Fallback: raw text display but extended
          return (
            <div className="detailed-description-section">
              {/* 🔷 POUR LES NŒUDS : Afficher les informations de base */}
              {!isTest(item) && (
                <div className="node-details-grid">
                  <div className="node-detail-item">
                    <span className="detail-label">📄 {t('pendingNodesPanel.labels.description')}</span>
                    <div className="detail-value">
                      {item.description || t('pendingNodesPanel.labels.noDescription')}
                    </div>
                  </div>
                  
                  {item.type && (
                    <div className="node-detail-item">
                      <span className="detail-label">🔧 {t('nodeForm.fields.nodeType')}</span>
                      <div className="detail-value detail-badge">{item.type}</div>
                    </div>
                  )}
                  
                  {item.technology && (
                    <div className="node-detail-item">
                      <span className="detail-label">⚙️ {t('nodeForm.fields.technology')}</span>
                      <div className="detail-value detail-badge">{item.technology}</div>
                    </div>
                  )}
                  
                  {item.featureTeam && (
                    <div className="node-detail-item">
                      <span className="detail-label">👥 {t('nodeForm.fields.featureTeam')}</span>
                      <div className="detail-value detail-badge">{item.featureTeam}</div>
                    </div>
                  )}
                  
                  {item.status && (
                    <div className="node-detail-item">
                      <span className="detail-label">📊 {t('nodeForm.fields.status')}</span>
                      <div className="detail-value detail-badge status-badge">{item.status}</div>
                    </div>
                  )}
                  
                  {item.author && (
                    <div className="node-detail-item">
                      <span className="detail-label">👤 {t('titles.author')}</span>
                      <div className="detail-value">{item.author}</div>
                    </div>
                  )}
                </div>
              )}
              
              {/* 🧪 POUR LES TESTS : Affichage simple */}
              {isTest(item) && (
                <>
                  <h5>📄 {t('pendingNodesPanel.labels.description')}</h5>
                  <div className="detailed-description-content">
                    {item.description || t('pendingNodesPanel.labels.noDescription')}
                  </div>
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* Actions étendues */}
      <div className="detailed-card-actions">
        <div className="detailed-actions-primary">
          {isTest(item) ? (
            <button
              className="detailed-action-btn validate test"
              onClick={() => handlers.handleValidateTestNode(item)}
              title={t('titles.validateAndIntegrateTest')}
            >
              <span className="action-icon">📚</span>
              <span className="action-text">{t('pendingNodesPanel.actions.validateTest')}</span>
            </button>
          ) : (
            <button
              className="detailed-action-btn validate node"
              onClick={() => handlers.handleValidateNode(item)}
              title={t('titles.validateAndIntegrateNode')}
            >
              <span className="action-icon">🌐</span>
              <span className="action-text">{t('pendingNodesPanel.actions.validateNode')}</span>
            </button>
          )}
        </div>

        <div className="detailed-actions-secondary">
          <button
            className="detailed-action-btn edit"
            onClick={() => handlers.handleEditNode(item)}
            title={t('titles.editElement')}
          >
            <span className="action-icon">✏️</span>
            <span className="action-text">{t('pendingNodesPanel.actions.edit')}</span>
          </button>

          {/* Bouton d'édition manuelle pour les tests créés manuellement */}
          {isTest(item) && item.metadata?.isManuallyCreated && handlers.onEditManualTest && (
            <button
              className="detailed-action-btn edit-manual"
              onClick={() => handlers.onEditManualTest(item)}
              title={t('pendingNodesPanel.tooltips.editManualTest')}
            >
              <span className="action-icon">🧪</span>
              <span className="action-text">{t('pendingNodesPanel.actions.manualEdit')}</span>
            </button>
          )}

          <button
            className="detailed-action-btn delete"
            onClick={() => handlers.handleDeleteNode(item.id)}
            title={t('pendingNodesPanel.tooltips.delete')}
          >
            <span className="action-icon">🗑️</span>
            <span className="action-text">{t('pendingNodesPanel.buttons.delete')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const KanbanView = ({ items, handlers, activeFilter, editingNodeId }) => {
  const { t } = useTranslation();

  // If we have a specific active filter, display a detailed single-column view
  /* if (activeFilter === 'urgent') {
    return (
      <div className="kanban-filtered-view">
        <DetailedSingleColumn
          title={t('titles.urgentElements')}
          items={items}
          handlers={handlers}
          variant="urgent"
          emptyMessage={t('pendingNodesPanel.emptyStates.noUrgentForNow')}
        />
      </div>
    );
  } */

  if (activeFilter === 'tests') {
    return (
      <div className="kanban-filtered-view">
        <DetailedSingleColumn
          title={t('titles.pendingTests')}
          items={items}
          handlers={handlers}
          variant="tests"
          emptyMessage="Aucun test en attente de validation"
        />
      </div>
    );
  }

  if (activeFilter === 'nodes') {
    return (
      <div className="kanban-filtered-view">
        <DetailedSingleColumn
          title={t('titles.pendingNodes')}
          items={items}
          handlers={handlers}
          variant="nodes"
          emptyMessage="Aucun nœud en attente d'intégration"
        />
      </div>
    );
  }

  // Default view (all): 3-column display
  return (
    <div className="kanban-board">
      {/* <div className="kanban-column urgent">
        <div className="column-header">
          <h4>🔥 {t('pendingNodesPanel.kanban.urgent')}</h4>
          <span className="column-count">{items.filter(needsAttention).length}</span>
        </div>
        <div className="column-content">
          {items.filter(needsAttention).map(item => (
            <KanbanCard key={item.id} item={item} handlers={handlers} />
          ))}
          {items.filter(needsAttention).length === 0 && (
            <div className="empty-column-message">
              <span className="empty-icon">✨</span>
              <span>{t('pendingNodesPanel.emptyStates.noUrgent')}</span>
            </div>
          )}
        </div>
      </div> */}

      <div className="kanban-column tests">
        <div className="column-header">
          <h4>🧪 {t('pendingNodesPanel.kanban.tests')}</h4>
          <span className="column-count">{items.filter(isTest).length}</span>
        </div>
        <div className="column-content">
          {items.filter(isTest).map(item => (
            <KanbanCard key={item.id} item={item} handlers={handlers} />
          ))}
          {items.filter(isTest).length === 0 && (
            <div className="empty-column-message">
              <span className="empty-icon">🧪</span>
              <span>{t('pendingNodesPanel.emptyStates.noTests')}</span>
            </div>
          )}
        </div>
      </div>

      <div className="kanban-column nodes">
        <div className="column-header">
          <h4>🔷 {t('pendingNodesPanel.kanban.nodes')}</h4>
          <span className="column-count">{items.filter(isNode).length}</span>
        </div>
        <div className="column-content">
          {items.filter(isNode).map(item => (
            <KanbanCard key={item.id} item={item} handlers={handlers} />
          ))}
          {items.filter(isNode).length === 0 && (
            <div className="empty-column-message">
              <span className="empty-icon">🔷</span>
              <span>{t('pendingNodesPanel.emptyStates.noNodes')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const KanbanCard = ({ item, handlers, isEditing = false }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [showAllScenarios, setShowAllScenarios] = useState(false);

  return (
    <div className={`kanban-card ${isEditing ? 'editing' : ''}`}>
      <div className="card-header">
        <div className="card-title">
          <span className="card-icon">{isTest(item) ? "🧪" : "🔷"}</span>
          <span className="card-name">{item.name}</span>
        </div>
        <div className="card-actions">
          <button
            className="seeNode action-btn preview"
            onClick={() => setShowDetails(!showDetails)}
            title={t('titles.viewDetails')}
          >
            👁️
          </button>
        </div>
      </div>

      <div className="card-meta">
        <span className="card-author">{item.author}</span>
        <span className="card-date">{new Date(item.createdAt).toLocaleDateString()}</span>
      </div>

      <div className="card-urgency">
        {(() => {
          const score = calculateUrgencyScore(item);
          const urgency = getUrgencyLevel(score, t);

          if (score >= 20) {
            return (
              <div
                className="urgency-badge"
                style={{ backgroundColor: urgency.color }}
                title={`Score d'urgence: ${score}/100`}
              >
                {urgency.label}
              </div>
            );
          }
          return null;
        })()}
      </div>

      {showDetails && (
        <div className="card-details">
          <div className="card-description">
            {(() => {
              // 1. Vérifier si on a des scénarios dans metadata
              if (Array.isArray(item.metadata?.scenarios) && item.metadata.scenarios.length > 0) {
                const scenarios = item.metadata.scenarios;
                const displayedScenarios = showAllScenarios ? scenarios : scenarios.slice(0, 3);

                return (
                  <div className="pending-scenarios-list">
                    <div className="scenarios-header">
                      <span className="scenarios-count">📋 {scenarios.length} scénario(s)</span>
                      {scenarios.length > 3 && (
                        <button
                          className="scenarios-toggle-btn"
                          onClick={() => setShowAllScenarios(!showAllScenarios)}
                        >
                          {showAllScenarios ? '📖 Voir moins' : '📖 Voir tout'}
                        </button>
                      )}
                    </div>

                    <div className={`scenarios-container ${showAllScenarios ? 'expanded' : 'collapsed'}`}>
                      {displayedScenarios.map((sc, i) => (
                        <ScenarioMiniCard key={sc.id || sc.uniqueKey || `scenario-c-${i}-${Date.now()}`} scenario={sc} />
                      ))}
                    </div>

                    {!showAllScenarios && scenarios.length > 3 && (
                      <div className="scenarios-more">
                        <button
                          className="show-more-btn"
                          onClick={() => setShowAllScenarios(true)}
                        >
                          📋 {t('pendingNodesPanel.kanban.showMoreScenarios', { count: scenarios.length - 3 })}
                        </button>
                      </div>
                    )}
                  </div>
                );
              }

              // 🚀 NOUVEAU : Détecter les tests générés avec steps mais sans scenarios  
              if (isTest(item) && (item.steps || item.metadata?.steps) && Array.isArray(item.steps || item.metadata?.steps) && (item.steps || item.metadata?.steps).length > 0) {
                const steps = item.steps || item.metadata?.steps;
                
                // Créer un scénario virtuel à partir des propriétés du test
                const virtualScenario = {
                  id: item.id || `virtual-${Date.now()}`,
                  title: item.name || item.title || 'Test généré',
                  type: item.metadata?.scenarioType || item.category || 'acceptance',
                  positive: item.positive !== false,
                  steps: steps,
                  targetNode: item.targetNode || item.metadata?.targetNode,
                  description: item.description
                };

                return (
                  <div className="pending-scenarios-list">
                    <div className="scenarios-header">
                      <span className="scenarios-count">🧪 Test généré ({steps.length} étapes)</span>
                    </div>

                    <div className="scenarios-container">
                      <ScenarioMiniCard scenario={virtualScenario} />
                    </div>
                  </div>
                );
              }

              // 2. Parser la description pour extraire les scénarios
              const parsedScenarios = item.scenarios || item.metadata?.scenarios || parseDescriptionForScenarios(item.description);
              if (parsedScenarios.length > 0) {
                const displayedScenarios = showAllScenarios ? parsedScenarios : parsedScenarios.slice(0, 3);

                return (
                  <div className="pending-scenarios-list">
                    <div className="scenarios-header">
                      <span className="scenarios-count">🔍 {parsedScenarios.length} scénario(s) détecté(s)</span>
                      {parsedScenarios.length > 3 && (
                        <button
                          className="scenarios-toggle-btn"
                          onClick={() => setShowAllScenarios(!showAllScenarios)}
                        >
                          {showAllScenarios ? '📖 Voir moins' : '📖 Voir tout'}
                        </button>
                      )}
                    </div>

                    <div className={`scenarios-container ${showAllScenarios ? 'expanded' : 'collapsed'}`}>
                      {displayedScenarios.map((sc, i) => (
                        <ScenarioMiniCard key={sc.id || sc.uniqueKey || `scenario-d-${i}-${Date.now()}`} scenario={sc} />
                      ))}
                    </div>

                    {!showAllScenarios && parsedScenarios.length > 3 && (
                      <div className="scenarios-more">
                        <button
                          className="show-more-btn"
                          onClick={() => setShowAllScenarios(true)}
                        >
                          📋 {t('pendingNodesPanel.kanban.showMoreScenarios', { count: parsedScenarios.length - 3 })}
                        </button>
                      </div>
                    )}
                  </div>
                );
              }

              // 3. Fallback: affichage texte brut mais stylé
              return (
                <div className="raw-description">
                  <div className="description-preview">
                    {item.description?.substring(0, 150)}
                    {item.description?.length > 150 && '...'}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <div className="card-footer">
        <div className="card-actions-main">
          {isTest(item) ? (
            <button
              className="action-btn validate test"
              onClick={() => handlers.handleValidateTestNode(item)}
              title={t('titles.validateTest')}
            >
              📚 {t('pendingNodesPanel.buttons.validate')}
            </button>
          ) : (
            <button
              className="action-btn validate node"
              onClick={() => handlers.handleValidateNode(item)}
              title={t('titles.validateNode')}
            >
              🌐 {t('pendingNodesPanel.buttons.validate')}
            </button>
          )}

          <button
            className="action-btn edit"
            onClick={() => handlers.handleEditNode(item)}
            title={t('titles.edit')}
          >
            ✏️
          </button>

          <button
            className="action-btn delete"
            onClick={() => handlers.handleDeleteNode(item.id)}
            title={t('titles.delete')}
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
};

const GridView = ({ items, handlers }) => (
  <div className="grid-view">
    {items.map((item) => (
      <div key={item.id} className="grid-item">
        <div className="grid-item-header">
          <span className="grid-item-icon">{isTest(item) ? "🧪" : "🔷"}</span>
          <span className="grid-item-name">{item.name}</span>
        </div>
        <div className="grid-item-meta">
          <span>{item.author}</span>
          <span>{new Date(item.createdAt).toLocaleDateString()}</span>
        </div>
        <div className="grid-item-description">
          {(() => {
            // 1. Vérifier les scénarios dans metadata d'abord
            if (Array.isArray(item.metadata?.scenarios) && item.metadata.scenarios.length > 0) {
              const scenarios = item.metadata.scenarios;
              return (
                <div className="grid-scenarios">
                  <span className="scenarios-count-small">📋 {scenarios.length}</span>
                  {scenarios.slice(0, 1).map((sc, i) => (
                    <ScenarioMiniCard key={sc.id || sc.uniqueKey || `grid-scenario-${i}-${Date.now()}`} scenario={sc} />
                  ))}
                </div>
              );
            }
            
            // 🚀 NOUVEAU : Détecter les tests générés avec steps
            if (isTest(item) && (item.steps || item.metadata?.steps) && Array.isArray(item.steps || item.metadata?.steps) && (item.steps || item.metadata?.steps).length > 0) {
              const steps = item.steps || item.metadata?.steps;
              
              // Créer un scénario virtuel à partir des propriétés du test
              const virtualScenario = {
                id: item.id || `virtual-${Date.now()}`,
                title: item.name || item.title || 'Test généré',
                type: item.metadata?.scenarioType || item.category || 'acceptance',
                positive: item.positive !== false,
                steps: steps,
                targetNode: item.targetNode || item.metadata?.targetNode,
                description: item.description
              };

              return (
                <div className="grid-scenarios">
                  <span className="scenarios-count-small">🧪 {steps.length} étapes</span>
                  <ScenarioMiniCard scenario={virtualScenario} />
                </div>
              );
            }
            
            // 2. Parser la description pour extraire les scénarios
            const scenarios = parseDescriptionForScenarios(item.description);
            if (scenarios.length > 0) {
              return (
                <div className="grid-scenarios">
                  <span className="scenarios-count-small">📋 {scenarios.length}</span>
                  {scenarios.slice(0, 1).map((sc, i) => (
                    <ScenarioMiniCard key={sc.id || sc.uniqueKey || `grid-scenario-${i}-${Date.now()}`} scenario={sc} />
                  ))}
                </div>
              );
            }
            
            // 3. Fallback: texte de description
            return <span className="grid-description">{item.description?.substring(0, 80)}...</span>;
          })()}
        </div>
        <div className="grid-item-actions">
          <button onClick={() => (isTest(item) ? handlers.handleValidateTestNode(item) : handlers.handleValidateNode(item))}>
            ✅
          </button>
          <button onClick={() => handlers.handleEditNode(item)}>✏️</button>
          {/* Bouton d'édition manuelle pour les tests créés manuellement */}
          {isTest(item) && item.metadata?.isManuallyCreated && handlers.onEditManualTest && (
            <button
              onClick={() => handlers.onEditManualTest(item)}
              title="Éditer avec le créateur de tests manuels"
            >
              🧪
            </button>
          )}
          <button onClick={() => handlers.handleDeleteNode(item.id)}>🗑️</button>
        </div>
      </div>
    ))}
  </div>
);

const ListView = ({ items, handlers }) => {
  const { t } = useTranslation();

  return (
    <div className="list-view">
      {items.map((item) => (
        <div key={item.id} className="list-item">
          <div className="list-item-content">
            <div className="list-item-header">
              <span className="list-item-icon">{isTest(item) ? "🧪" : "🔷"}</span>
              <span className="list-item-name">{item.name}</span>
              <span className="list-item-author">{item.author}</span>
              <span className="list-item-date">{new Date(item.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="list-item-description">
              {item.description?.substring(0, 150)}...
            </div>
          </div>
          <div className="list-item-actions">
            <button onClick={() => (isTest(item) ? handlers.handleValidateTestNode(item) : handlers.handleValidateNode(item))}>
              ✅ {t('pendingNodesPanel.buttons.validate')}
            </button>
            <button onClick={() => handlers.handleEditNode(item)}>✏️ {t('pendingNodesPanel.buttons.edit')}</button>
            {/* Bouton d'édition manuelle pour les tests créés manuellement */}
            {isTest(item) && item.metadata?.isManuallyCreated && handlers.onEditManualTest && (
              <button
                onClick={() => handlers.onEditManualTest(item)}
                title={t('pendingNodesPanel.tooltips.editManualTest')}
              >
                🧪 {t('pendingNodesPanel.buttons.manualEdit')}
              </button>
            )}
            <button onClick={() => handlers.handleDeleteNode(item.id)}>🗑️ {t('pendingNodesPanel.buttons.delete')}</button>
          </div>
        </div>
      ))}
    </div>
  );
};

// === COMPOSANT PRINCIPAL ===
const PendingNodesPanel = ({
  pendingNodes,
  nodes,
  handleValidateTestNode,
  handleValidateNode,
  handleEditNode,
  handleDeleteNode,
  onUpdatePendingNode, // 👈 NOUVEAU CALLBACK
  onEditManualTest, // 👈 NEW: Callback to edit with manual creator
}) => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState("kanban");
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // 👇 NOUVEAUX ÉTATS POUR LE PANNEAU D'ÉDITION
  const [editingNode, setEditingNode] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  // 👇 NOUVELLE FONCTION D'ÉDITION LOCALE
  const handleEditNodeLocal = (node) => {
    setEditingNode(node);
    
    // 🚀 DÉTECTION INTELLIGENTE DES SCÉNARIOS pour les tests générés
    let scenarios = [];
    
    // 1. D'abord vérifier les scénarios dans metadata
    if (Array.isArray(node.metadata?.scenarios) && node.metadata.scenarios.length > 0) {
      scenarios = node.metadata.scenarios;
    } 
    // 2. Détecter les tests générés avec steps
    else if (isTest(node) && (node.steps || node.metadata?.steps) && Array.isArray(node.steps || node.metadata?.steps)) {
      const steps = node.steps || node.metadata?.steps;
      // Créer un scénario virtuel à partir des propriétés du test
      scenarios = [{
        id: node.id || `virtual-${Date.now()}`,
        title: node.name || node.title || 'Test généré',
        type: node.metadata?.scenarioType || node.category || 'acceptance',
        positive: node.positive !== false,
        steps: steps,
        targetNode: node.targetNode || node.metadata?.targetNode,
        description: node.description
      }];
    }
    // 3. Sinon parser la description
    else {
      scenarios = node.scenarios || parseDescriptionForScenarios(node.description);
    }
    
    setEditFormData({
      name: node.name || '',
      description: node.description || '',
      author: node.author || '',
      type: node.type || '',  // 👈 NOUVEAU : Type du nœud
      technology: node.technology || '',  // 👈 NOUVEAU : Technologie
      featureTeam: node.featureTeam || '',  // 👈 NOUVEAU : Feature team
      status: node.status || '',  // 👈 NOUVEAU : Statut
      priority: node.metadata?.priority || 'normal',
      tags: node.metadata?.tags || [],
      scenarios: scenarios,
      targetNode: node.targetNode || node.metadata?.targetNode || '', // 👈 NOUVEAU: Initialiser le nœud cible
      metadata: node.metadata || {},  // 👈 NOUVEAU : Métadonnées complètes
      endpointSchema: node.endpointSchema || {}  // 👈 NOUVEAU : Schéma endpoints
    });
    setShowEditPanel(true);
    setIsDirty(false);
  };

  // 👇 FERMER LE PANNEAU D'ÉDITION
  const closeEditPanel = () => {
    if (isDirty) {
      const confirmClose = window.confirm(t('placeholders.confirmUnsavedChanges'));
      if (!confirmClose) return;
    }
    setShowEditPanel(false);
    setEditingNode(null);
    setEditFormData({});
    setIsDirty(false);
  };

  // 👇 SAUVEGARDER LES MODIFICATIONS
  const saveEditedNode = async () => {
    try {
      const updatedNode = {
        ...editingNode,
        name: editFormData.name,
        description: editFormData.description,
        author: editFormData.author,
        type: editFormData.type,  // 👈 NOUVEAU : Type du nœud
        technology: editFormData.technology,  // 👈 NOUVEAU : Technologie
        featureTeam: editFormData.featureTeam,  // 👈 NOUVEAU : Feature team
        status: editFormData.status,  // 👈 NOUVEAU : Statut
        targetNode: editFormData.targetNode, // 👈 NOUVEAU: Sauvegarder le nœud cible
        metadata: {
          ...editingNode.metadata,
          priority: editFormData.priority,
          tags: editFormData.tags,
          scenarios: editFormData.scenarios,
          targetNode: editFormData.targetNode // 👈 AUSSI dans les métadonnées pour compatibilité
        },
        endpointSchema: editFormData.endpointSchema  // 👈 NOUVEAU : Schéma endpoints
      };

      // 👇 UTILISER SEULEMENT LE CALLBACK DE MISE À JOUR
      if (typeof onUpdatePendingNode === 'function') {
        onUpdatePendingNode(updatedNode);
      }

      setIsDirty(false);
      setShowEditPanel(false);
      setEditingNode(null);

      // console.log('✅ Node sauvegardé avec succès:', updatedNode); // COMMENTÉ - contient données sensibles
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
    }
  };

  // 👇 GÉRER LES CHANGEMENTS DANS LE FORMULAIRE
  const handleFieldChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(true);
  };

  const handlers = {
    handleValidateTestNode,
    handleValidateNode,
    handleEditNode: handleEditNodeLocal, // 👈 Utiliser la fonction locale
    handleDeleteNode,
    onEditManualTest, // 👈 NOUVEAU: Ajouter le callback pour l'édition manuelle
  };

  // Filtrer les items selon le filtre actif
  const getFilteredItems = () => {
    let filtered = pendingNodes;

    /* if (activeFilter === "urgent") {
      filtered = filtered.filter(needsAttention);
    } else */ if (activeFilter === "tests") {
      filtered = filtered.filter(isTest);
    } else if (activeFilter === "nodes") {
      filtered = filtered.filter(isNode);
    }

    // Filtre de recherche amélioré
    if (searchTerm && searchTerm.trim()) {
      const searchQuery = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(
        (item) => {
          // Chercher dans le nom, la description, le type et les tags
          const name = (item.name || "").toLowerCase();
          const description = (item.description || "").toLowerCase();
          const type = (item.type || "").toLowerCase();
          const tags = (item.metadata?.tags || []).map(t => (t || "").toLowerCase()).join(" ");
          
          // Retourner vrai si la recherche correspond à n'importe quel champ
          return (
            name.includes(searchQuery) || 
            description.includes(searchQuery) || 
            type.includes(searchQuery) ||
            tags.includes(searchQuery)
          );
        }
      );
    }

    return filtered;
  };

  // Séparer et classer les éléments - en appliquant aussi le filtre searchTerm
  const getFilteredByType = (filterFn) => {
    let filtered = pendingNodes.filter(filterFn);
    
    if (searchTerm && searchTerm.trim()) {
      const searchQuery = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(
        (item) => {
          const name = (item.name || "").toLowerCase();
          const description = (item.description || "").toLowerCase();
          const type = (item.type || "").toLowerCase();
          const tags = (item.metadata?.tags || []).map(t => (t || "").toLowerCase()).join(" ");
          
          return (
            name.includes(searchQuery) || 
            description.includes(searchQuery) || 
            type.includes(searchQuery) ||
            tags.includes(searchQuery)
          );
        }
      );
    }
    
    return filtered;
  };

  const categorizedItems = {
    urgent: getFilteredByType(needsAttention),
    tests: getFilteredByType(isTest),
    nodes: getFilteredByType(isNode),
    recent: pendingNodes.slice(0, 5),
  };

  const handleBulkValidate = () => {
    const itemsToValidate = getFilteredItems();
    itemsToValidate.forEach(item => {
      if (isTest(item)) {
        handleValidateTestNode(item);
      } else {
        handleValidateNode(item);
      }
    });
  };

  return (
    <div id="section-pending-nodes">
      <div className={`pending-panel-modern ${showEditPanel ? 'with-edit-panel' : ''}`}>
        {/* === HEADER === */}
        <div className="pending-header-modern">
          <div className="header-left">
            <h3 className="panel-title">
              <span className="title-icon">🧱</span>
              {t('pendingNodesPanel.inModeling')}
            </h3>
            <div className="quick-stats">
              {/* <StatsBadge count={categorizedItems.urgent.length} label={t('pendingNodesPanel.labels.urgent')} variant="urgent" /> */}
              <StatsBadge count={categorizedItems.tests.length} label={t('pendingNodesPanel.labels.tests')} variant="tests" />
              <StatsBadge count={categorizedItems.nodes.length} label={t('pendingNodesPanel.labels.nodes')} variant="nodes" />
            </div>
          </div>

          <div className="header-controls">
            {/* Barre de recherche */}
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder={t('pendingNodesPanel.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Sélecteur de vue */}
            <div className="view-selector">
              <button
                className={`view-btn ${activeView === "kanban" ? "active" : ""}`}
                onClick={() => setActiveView("kanban")}
                title={t('pendingNodesPanel.views.kanban')}
              >
                📋
              </button>

            </div>
          </div>
        </div>

        {/* === FILTRES RAPIDES === */}
        <div className="quick-filters">
          <FilterButton
            active={activeFilter === "all"}
            onClick={() => setActiveFilter("all")}
            count={pendingNodes.length}
          >
            {t('pendingNodesPanel.filters.all')}
          </FilterButton>
          {/* <FilterButton
            active={activeFilter === "urgent"}
            onClick={() => setActiveFilter("urgent")}
            count={categorizedItems.urgent.length}
            variant="urgent"
          >
            {t('pendingNodesPanel.filters.urgent')}
          </FilterButton> */}
          <FilterButton
            active={activeFilter === "tests"}
            onClick={() => setActiveFilter("tests")}
            count={categorizedItems.tests.length}
            variant="tests"
          >
            {t('pendingNodesPanel.filters.tests')}
          </FilterButton>
          <FilterButton
            active={activeFilter === "nodes"}
            onClick={() => setActiveFilter("nodes")}
            count={categorizedItems.nodes.length}
            variant="nodes"
          >
            {t('pendingNodesPanel.filters.nodes')}
          </FilterButton>
        </div>

        {/* === CONTENU PRINCIPAL AVEC PANNEAU D'ÉDITION === */}
        <div className="main-content-container">
          {/* === COLONNE PRINCIPALE === */}
          <div className="content-column">
            {/* === CONTENU SELON LA VUE === */}
            <div className="pending-content">
              {activeView === "kanban" && (
                <KanbanView
                  items={getFilteredItems()}
                  handlers={handlers}
                  activeFilter={activeFilter}
                  editingNodeId={editingNode?.id} // 👈 Passer l'ID du nœud en édition
                />
              )}

              {activeView === "grid" && (
                <GridView
                  items={getFilteredItems()}
                  handlers={handlers}
                  editingNodeId={editingNode?.id}
                />
              )}

              {activeView === "list" && (
                <ListView
                  items={getFilteredItems()}
                  handlers={handlers}
                  editingNodeId={editingNode?.id}
                />
              )}
            </div>

            {/* === ACTIONS EN BULK === */}
            {getFilteredItems().length > 0 && (
              <div className="bulk-actions">
                <button className="bulk-btn validate-all" onClick={handleBulkValidate}>
                  ✅ {t('pendingNodesPanel.bulkActions.validateAll', { count: getFilteredItems().length })}
                </button>
                <button className="bulk-btn archive-all">📦 {t('pendingNodesPanel.bulkActions.archiveAll')}</button>
              </div>
            )}
          </div>

          {/* === PANNEAU D'ÉDITION LATÉRAL === */}
          {showEditPanel && editingNode && (
            <EditSidePanel
              node={editingNode}
              formData={editFormData}
              isDirty={isDirty}
              nodes={nodes}
              onFieldChange={handleFieldChange}
              onSave={saveEditedNode}
              onClose={closeEditPanel}
            />
          )}
        </div>

        {/* === LÉGENDE === */}
        { /*  <div className="urgency-legend">
          <h5>{t('titles.urgencyLevelsLegend')}</h5>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-badge" style={{ backgroundColor: '#dc2626' }}>🔥 Critique</div>
              <span>60-100 points : Action immédiate requise</span>
            </div>
            <div className="legend-item">
              <div className="legend-badge" style={{ backgroundColor: '#ea580c' }}>⚠️ Urgent</div>
              <span>40-59 points : À traiter rapidement</span>
            </div>
            <div className="legend-item">
              <div className="legend-badge" style={{ backgroundColor: '#d97706' }}>🟡 Attention</div>
              <span>20-39 points : Nécessite un suivi</span>
            </div>
            <div className="legend-item">
              <div className="legend-badge" style={{ backgroundColor: '#16a34a' }}>✅ Normal</div>
              <span>0-19 points : Priorité standard</span>
            </div>
            <div className="legend-item">
              <div className="legend-badge" style={{ backgroundColor: '#3b82f6' }}>🧪 Tests</div>
              <span>Indique que le nœud contient des tests</span>
            </div>
          </div>
          <div className="legend-calculation">
            <details>
              <summary>🧮 {t('pendingNodes.scoreCalculation.title')}</summary>
              <div className="calculation-details">
                <p><strong>{t('pendingNodes.scoreCalculation.factorsTitle')}:</strong></p>
                <ul>
                  <li>📅 <strong>{t('pendingNodes.scoreCalculation.age')}:</strong> {t('pendingNodes.scoreCalculation.ageDetails')}</li>
                  <li>🔖 <strong>{t('pendingNodes.scoreCalculation.priority')}:</strong> {t('pendingNodes.scoreCalculation.priorityDetails')}</li>
                  <li>❌ <strong>{t('pendingNodes.scoreCalculation.errors')}:</strong> {t('pendingNodes.scoreCalculation.errorsDetails')}</li>
                  <li>🚧 <strong>{t('pendingNodes.scoreCalculation.blocking')}:</strong> {t('pendingNodes.scoreCalculation.blockingDetails')}</li>
                  <li>🔑 <strong>{t('pendingNodes.scoreCalculation.keywords')}:</strong> {t('pendingNodes.scoreCalculation.keywordsDetails')}</li>
                  <li>📊 <strong>{t('pendingNodes.scoreCalculation.complexity')}:</strong> {t('pendingNodes.scoreCalculation.complexityDetails')}</li>
                </ul>
              </div>
            </details>
          </div>
        </div>*/}
      </div>
    </div>
  );
};

// Ajouter ce composant avant EditSidePanel :

const EditableScenarioCard = ({ scenario, index, nodes = [], onUpdate, onDelete }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedScenario, setEditedScenario] = useState(scenario);

  const handleSaveScenario = () => {
    // console.log('💾 Sauvegarde du scénario:', editedScenario); // COMMENTÉ - contient données sensibles
    onUpdate(editedScenario);
    setEditedScenario(editedScenario);
    setIsEditing(false);
    console.log('✅ Scénario sauvegardé');
  };

  const handleCancel = () => {
    setEditedScenario(scenario);
    setIsEditing(false);
  };

  const updateStep = (stepIndex, field, value) => {
    const newSteps = [...editedScenario.steps];
    newSteps[stepIndex] = {
      ...newSteps[stepIndex],
      [field]: value
    };
    setEditedScenario({
      ...editedScenario,
      steps: newSteps
    });
  };

  const addStep = () => {
    const newSteps = [...editedScenario.steps, { type: 'And', text: '' }];
    setEditedScenario({
      ...editedScenario,
      steps: newSteps
    });
  };

  const removeStep = (stepIndex) => {
    const newSteps = editedScenario.steps.filter((_, idx) => idx !== stepIndex);
    setEditedScenario({
      ...editedScenario,
      steps: newSteps
    });
  };

  const moveStep = (stepIndex, direction) => {
    const newSteps = [...editedScenario.steps];
    const targetIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;

    if (targetIndex >= 0 && targetIndex < newSteps.length) {
      [newSteps[stepIndex], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[stepIndex]];
      setEditedScenario({
        ...editedScenario,
        steps: newSteps
      });
    }
  };

  return (
    <div className="editable-scenario-card">
      {/* En-tête du scénario */}
      <div className="scenario-card-header">
        <div className="scenario-info">
          <span className="scenario-number">#{index + 1}</span>
          {isEditing ? (
            <input
              type="text"
              value={editedScenario.title}
              onChange={(e) => setEditedScenario({
                ...editedScenario,
                title: e.target.value
              })}
              className="scenario-title-input"
              placeholder="Titre du scénario..."
            />
          ) : (
            <h4 className="scenario-title">{scenario.title}</h4>
          )}
        </div>

        <div className="scenario-actions">
          {isEditing ? (
            <>
              <button className="save-btn" onClick={handleSaveScenario} title={t('pendingNodesPanel.scenarios.saveScenario')}>
                ✅
              </button>
              <button className="cancel-btn" onClick={handleCancel} title={t('actions.cancel')}>
                ❌
              </button>
            </>
          ) : (
            <>
              <button
                className="edit-btn"
                onClick={() => setIsEditing(true)}
                title={t('pendingNodesPanel.scenarios.editScenario')}
              >
                ✏️
              </button>
              <button
                className="delete-btn"
                onClick={() => {
                  if (window.confirm(t('placeholders.confirmDeleteScenario'))) {
                    onDelete();
                  }
                }}
                title={t('pendingNodesPanel.scenarios.deleteScenario')}
              >
                🗑️
              </button>
            </>
          )}
        </div>
      </div>

      {/* Étapes Gherkin */}
      <div className="scenario-steps-editor">
        <h5>📝 Étapes Gherkin</h5>

        {isEditing ? (
          <div className="steps-edit-mode">
            {editedScenario.steps.map((step, stepIdx) => (
              <div key={`step-edit-${stepIdx}-${Date.now()}`} className="step-editor-row">
                {/* Sélecteur du type d'étape */}
                <select
                  value={step.type}
                  onChange={(e) => updateStep(stepIdx, 'type', e.target.value)}
                  className="step-type-selector"
                >
                  <option value="Given">Given</option>
                  <option value="When">When</option>
                  <option value="Then">Then</option>
                  <option value="And">And</option>
                  <option value="But">But</option>
                </select>

                {/* Zone d'édition du texte */}
                <input
                  type="text"
                  value={step.text}
                  onChange={(e) => updateStep(stepIdx, 'text', e.target.value)}
                  className="step-text-input"
                  placeholder="Décrivez cette étape..."
                />

                {/* Actions sur l'étape */}
                <div className="step-actions">
                  <button
                    className="move-step-btn"
                    onClick={() => moveStep(stepIdx, 'up')}
                    disabled={stepIdx === 0}
                    title={t('pendingNodesPanel.scenarios.moveUp')}
                  >
                    ⬆️
                  </button>
                  <button
                    className="move-step-btn"
                    onClick={() => moveStep(stepIdx, 'down')}
                    disabled={stepIdx === editedScenario.steps.length - 1}
                    title={t('pendingNodesPanel.scenarios.moveDown')}
                  >
                    ⬇️
                  </button>
                  <button
                    className="remove-step-btn"
                    onClick={() => removeStep(stepIdx)}
                    title={t('pendingNodesPanel.scenarios.deleteStep')}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}

            {/* Bouton ajouter une étape */}
            <button className="add-step-btn" onClick={addStep}>
              ➕ {t('pendingNodesPanel.scenarios.addStep')}
            </button>
          </div>
        ) : (
          <div className="steps-view-mode">
            {scenario.steps?.map((step, stepIdx) => (
              <div key={`step-view-${stepIdx}-${Date.now()}`} className="gherkin-step-display">
                <span className={`gherkin-keyword ${step.type?.toLowerCase()}`}>
                  {step.type}
                </span>
                <span className="gherkin-text">{step.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Affichage du nœud cible en mode lecture */}
      {!isEditing && scenario.targetNode && (
        <div className="target-node-display">
          <div className="target-node-info">
            <span className="target-node-badge">
              🔗 Associé à: <strong>{scenario.targetNode}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Métadonnées du scénario */}
      {isEditing && (
        <div className="scenario-metadata-editor">
          <h5>🔖 Propriétés</h5>
          <div className="metadata-row">
            <div className="metadata-field">
              <label>{t('pendingNodesPanel.scenarios.type')} :</label>
              <select
                value={editedScenario.type || 'Fonctionnel'}
                onChange={(e) => setEditedScenario({
                  ...editedScenario,
                  type: e.target.value
                })}
              >
                <option value="Fonctionnel">🎯 {t('pendingNodesPanel.scenarios.types.functional')}</option>
                <option value="Sécurité">🔐 {t('pendingNodesPanel.scenarios.types.security')}</option>
                <option value="Performance">⚡ {t('pendingNodesPanel.scenarios.types.performance')}</option>
                <option value="Intégration">🔗 {t('pendingNodesPanel.scenarios.types.integration')}</option>
              </select>
            </div>

            <div className="metadata-field">
              <label>{t('pendingNodesPanel.scenarios.expectedResult')} :</label>
              <select
                value={editedScenario.positive ? 'positive' : 'negative'}
                onChange={(e) => setEditedScenario({
                  ...editedScenario,
                  positive: e.target.value === 'positive',
                  testType: e.target.value
                })}
              >
                <option value="positive">✅ {t('pendingNodesPanel.scenarios.positive')}</option>
                <option value="negative">❌ {t('pendingNodesPanel.scenarios.negative')}</option>
              </select>
            </div>

            <div className="metadata-field">
              <label>🎯 {t('pendingNodesPanel.scenarios.targetNode')} :</label>
              <select
                value={editedScenario.targetNode || ''}
                onChange={(e) => setEditedScenario({
                  ...editedScenario,
                  targetNode: e.target.value
                })}
                className="target-node-select"
              >
                <option value="">📋 {t('pendingNodesPanel.scenarios.noNodeAssociated')}</option>
                {nodes && nodes.length > 0 ? nodes.map((node) => (
                  <option key={node.id || node.name} value={node.name}>
                    🔷 {node.name}
                    {node.type && ` (${node.type})`}
                  </option>
                )) : (
                  <option disabled>⚠️ {t('pendingNodesPanel.scenarios.noNodesAvailable')}</option>
                )}
              </select>
              {editedScenario.targetNode && (
                <div className="target-node-info">
                  <span className="target-node-badge">
                    🔗 Associé à: <strong>{editedScenario.targetNode}</strong>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EditSidePanel = ({ node, formData, isDirty, nodes = [], onFieldChange, onSave, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('general');
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = t('pendingNodesPanel.validation.nameRequired');
    }

    if (!formData.author?.trim()) {
      newErrors.author = t('pendingNodesPanel.validation.authorRequired');
    }

    // 🔥 Description requise SEULEMENT pour les nœuds, PAS pour les tests
    if (!isTest(node) && !formData.description?.trim()) {
      newErrors.description = 'La description est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveAll = () => {
    if (validateForm()) {
      onSave(); // Sauvegarde tout le nœud
    } else {
      console.warn('Validation échouée, vérifiez les champs requis');
    }
  };

  const addTag = (tag) => {
    if (tag && !formData.tags?.includes(tag)) {
      onFieldChange('tags', [...(formData.tags || []), tag]);
    }
  };

  const removeTag = (tagToRemove) => {
    onFieldChange('tags', (formData.tags || []).filter(tag => tag !== tagToRemove));
  };

  return (
    <div className="edit-side-panel">
      {/* === HEADER DU PANNEAU === */}
      <div className="edit-panel-header">
        <div className="edit-panel-title">
          <span className="panel-icon">{isTest(node) ? '🧪' : '🔷'}</span>
          <div className="title-section">
            <h3>{t('pendingNodesPanel.editPanel.title', { type: isTest(node) ? t('pendingNodesPanel.labels.test') : t('pendingNodesPanel.labels.node') })}</h3>
            <span className="node-id">ID: {node.id}</span>
          </div>
        </div>

        <div className="panel-actions">
          {isDirty && (
            <div className="unsaved-indicator">
              <span className="unsaved-dot"></span>
              Non sauvegardé
            </div>
          )}
          <button className="close-panel-btn" onClick={onClose} title="Fermer">
            ✕
          </button>
        </div>
      </div>

      {/* === TABS DU PANNEAU === */}
      <div className="edit-panel-tabs">
        <button
          className={`panel-tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          📝 {t('pendingNodesPanel.tabs.general')}
        </button>
        <button
          className={`panel-tab ${activeTab === 'scenarios' ? 'active' : ''}`}
          onClick={() => setActiveTab('scenarios')}
        >
          🧪 {t('pendingNodesPanel.tabs.scenarios')} ({formData.scenarios?.length || 0})
        </button>
        <button
          className={`panel-tab ${activeTab === 'metadata' ? 'active' : ''}`}
          onClick={() => setActiveTab('metadata')}
        >
          🔖 Métadonnées
        </button>
      </div>

      {/* === CONTENU DU PANNEAU === */}
      <div className="edit-panel-content">
        {/* === TAB GÉNÉRAL === */}
        {activeTab === 'general' && (
          <div className="tab-content">
            <div className="form-group">
              <label className="form-label required">{t('titles.name')}</label>
              <input
                type="text"
                className={`node-form-input ${errors.name ? 'error' : ''}`}
                value={formData.name || ''}
                onChange={(e) => onFieldChange('name', e.target.value)}
                placeholder={t('pendingNodesPanel.placeholders.nodeName')}
                maxLength={100}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label className="form-label required">{t('titles.author')}</label>
              <input
                type="text"
                className={`node-form-input ${errors.author ? 'error' : ''}`}
                value={formData.author || ''}
                onChange={(e) => onFieldChange('author', e.target.value)}
                placeholder={t('pendingNodesPanel.placeholders.authorName')}
                maxLength={100}
              />
              {errors.author && <span className="error-message">{errors.author}</span>}
            </div>

            {/* 🔥 CONDITION : Afficher description SEULEMENT pour les nœuds, PAS pour les tests */}
            {!isTest(node) && (
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label className="form-label required" style={{ margin: 0 }}>{t('titles.description')}</label>
                  <span className={`char-counter-edit ${(formData.description || '').length > 900 ? 'critical' : (formData.description || '').length > 800 ? 'warning' : ''}`}>
                    {(formData.description || '').length}/1000
                  </span>
                </div>
                <textarea
                  className={`node-form-textarea ${errors.description ? 'error' : ''}`}
                  value={formData.description || ''}
                  onChange={(e) => onFieldChange('description', e.target.value)}
                  placeholder="Description détaillée..."
                  rows={10}
                  maxLength={1000}
                />
                {errors.description && <span className="error-message">{errors.description}</span>}
                <div className="form-help">
                  💡Describe context of the feature, please, to help your coworkers !
                </div>
              </div>
            )}

            {/* 🔷 POUR LES NŒUDS : Afficher les champs spécifiques */}
            {!isTest(node) && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">{t('nodeForm.fields.nodeType')}</label>
                    <input
                      type="text"
                      className="node-form-input"
                      value={formData.type || ''}
                      onChange={(e) => onFieldChange('type', e.target.value)}
                      placeholder="Ex: Feature, Module, API..."
                      maxLength={100}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">{t('nodeForm.fields.status')}</label>
                    <select
                      className="node-form-select"
                      value={formData.status || ''}
                      onChange={(e) => onFieldChange('status', e.target.value)}
                    >
                      <option value="">{t('nodeForm.selectStatus')}</option>
                      <option value="En développement">🚧 {t('nodeForm.statuses.inDevelopment')}</option>
                      <option value="En test">🧪 {t('nodeForm.statuses.testing')}</option>
                      <option value="Terminé">✅ {t('nodeForm.statuses.completed')}</option>
                      <option value="En attente">⏸️ {t('nodeForm.statuses.waiting')}</option>
                      <option value="En modélisation">🎯 En modélisation</option>
                      <option value="Prod">🚀 {t('nodeForm.statuses.production')}</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('nodeForm.fields.technology')}</label>
                    <input
                      type="text"
                      className="node-form-input"
                      value={formData.technology || ''}
                      onChange={(e) => onFieldChange('technology', e.target.value)}
                      placeholder={t('nodeForm.placeholders.technology')}
                      maxLength={200}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">{t('nodeForm.fields.featureTeam')}</label>
                    <input
                      type="text"
                      className="node-form-input"
                      value={formData.featureTeam || ''}
                      onChange={(e) => onFieldChange('featureTeam', e.target.value)}
                      placeholder={t('nodeForm.placeholders.featureTeam')}
                      maxLength={150}
                    />
                  </div>
                </div>
              </>
            )}

            {/* 🧪 POUR LES TESTS : Afficher un résumé des scénarios */}
            {isTest(node) && (
              <div className="test-summary-section">
                <div className="test-summary-header">
                  <h4>🧪 Résumé du test</h4>
                  <span className="scenarios-count-badge">
                    {formData.scenarios?.length || 0} scénario(s)
                  </span>
                </div>
                <div className="test-summary-content">
                  <p>{t('pendingNodesPanel.editPanel.testScenariosInfo', { count: formData.scenarios?.length || 0 })}</p>

                  {formData.scenarios?.length > 0 && (
                    <div className="scenarios-preview">
                      <h5>📋 {t('pendingNodesPanel.editPanel.scenariosPreview')}</h5>
                      <ul className="scenarios-titles-list">
                        {formData.scenarios.slice(0, 3).map((scenario, idx) => (
                          <li key={scenario.id || scenario.uniqueKey || `preview-scenario-${idx}-${Date.now()}`} className="scenario-title-item">
                            <span className="scenario-icon">🧪</span>
                            <span className="scenario-name">{scenario.title || `Scénario ${idx + 1}`}</span>
                            <span className="scenario-steps-count">({scenario.steps?.length || 0} étapes)</span>
                          </li>
                        ))}
                        {formData.scenarios.length > 3 && (
                          <li className="more-scenarios">
                            ... {t('pendingNodesPanel.editPanel.andMore', { count: formData.scenarios.length - 3 })}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* === TAB SCÉNARIOS === */}
        {activeTab === 'scenarios' && (
          <div className="tab-content">
            <div className="scenarios-section">
              <div className="scenarios-header">
                <h4>🧪 {t('pendingNodesPanel.tabs.scenarios')}</h4>
                <button
                  className="refresh-scenarios-btn"
                  onClick={() => {
                    const newScenarios = parseDescriptionForScenarios(formData.description);
                    onFieldChange('scenarios', newScenarios);
                  }}
                >
                  🔄 Actualiser
                </button>
              </div>

              {(!formData.scenarios || formData.scenarios.length === 0) ? (
                <div className="no-scenarios">
                  <div className="no-scenarios-icon">🔍</div>
                  <p>Aucun scénario détecté</p>
                  <small>{t('pendingNodesPanel.editPanel.addScenariosInDescription')}</small>
                </div>
              ) : (
                <div className="scenarios-list">
                  {formData.scenarios.map((scenario, index) => (
                    <EditableScenarioCard
                      key={scenario.id || scenario.uniqueKey || `editable-scenario-${index}-${Date.now()}`}
                      scenario={scenario}
                      index={index}
                      nodes={nodes}
                      onUpdate={(updatedScenario) => {
                        const newScenarios = [...formData.scenarios];
                        newScenarios[index] = updatedScenario;
                        onFieldChange('scenarios', newScenarios);
                      }}
                      onDelete={() => {
                        const newScenarios = formData.scenarios.filter((_, i) => i !== index);
                        onFieldChange('scenarios', newScenarios);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* === TAB MÉTADONNÉES === */}
        {activeTab === 'metadata' && (
          <div className="tab-content">
            <div className="form-group">
              <label className="form-label">{t('titles.priority')}</label>
              <select
                className="node-form-select"
                value={formData.priority || 'normal'}
                onChange={(e) => onFieldChange('priority', e.target.value)}
              >
                <option value="low">{t('pendingNodesPanel.metadata.priority.low')}</option>
                <option value="normal">{t('pendingNodesPanel.metadata.priority.normal')}</option>
                <option value="high">{t('pendingNodesPanel.metadata.priority.high')}</option>
                <option value="urgent">{t('pendingNodesPanel.metadata.priority.urgent')}</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{t('titles.tags')} (Max 10)</label>
              <div className="tags-input-container">
                <div className="tags-list">
                  {(formData.tags || []).map(tag => (
                    <span key={tag} className="tag">
                      {tag}
                      <button
                        className="tag-remove"
                        onClick={() => removeTag(tag)}
                        type="button"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  className="node-form-input tag-input"
                  placeholder={t('pendingNodesPanel.placeholders.addTag')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if ((formData.tags || []).length < 10) {
                        addTag(e.target.value.trim());
                        e.target.value = '';
                      }
                    }
                  }}
                  disabled={(formData.tags || []).length >= 10}
                  maxLength={50}
                />
              </div>
              <div className="char-counter-container">
                <span className="char-count-info">
                  <span className={`char-counter-edit ${(formData.tags || []).length >= 10 ? 'critical' : ''}`}>
                    🏷️ {(formData.tags || []).length}/10 tags
                  </span>
                </span>
              </div>
            </div>

            {/* Nœud cible (seulement pour les tests) */}
            {isTest(node) && (
              <div className="form-group">
                <label className="form-label">{t('titles.targetNode')}</label>
                <select
                  className="node-form-select"
                  value={formData.targetNode || ''}
                  onChange={(e) => onFieldChange('targetNode', e.target.value)}
                >
                  <option value="">{t('pendingNodesPanel.metadata.targetNode.none')}</option>
                  {nodes && nodes.length > 0 ? nodes.map((availableNode) => (
                    <option key={availableNode.id || availableNode.name} value={availableNode.name}>
                      🔷 {availableNode.name}
                      {availableNode.type && ` (${availableNode.type})`}
                    </option>
                  )) : (
                    <option disabled>{t('pendingNodesPanel.metadata.targetNode.noNodesAvailable')}</option>
                  )}
                </select>
                {formData.targetNode && (
                  <div className="target-node-info">
                    <span className="target-node-badge">
                      🔗 Test associé à: <strong>{formData.targetNode}</strong>
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="metadata-summary">
              <h4>{t('pendingNodesPanel.metadata.summary.title')}</h4>
              <div className="summary-grid">
                <div className="summary-item">
                  <span className="summary-label">{t('pendingNodesPanel.metadata.summary.type')}</span>
                  <span className="summary-value">{node?.type || t('pendingNodesPanel.metadata.summary.notDefined')}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">{t('pendingNodesPanel.metadata.summary.age')}</span>
                  <span className="summary-value">{calculateDaysOld(node)} {t('pendingNodesPanel.metadata.summary.days')}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">{t('titles.urgencyScore')}</span>
                  <span className="summary-value">{calculateUrgencyScore(node)}/100</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">{t('pendingNodesPanel.tabs.scenarios')}</span>
                  <span className="summary-value">{formData.scenarios?.length || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* === FOOTER DU PANNEAU === */}
      <div className="edit-panel-footer">
        <button className="btn-secondary" onClick={onClose}>
          ❌ {t('actions.cancel')}
        </button>
        <button
          className={`btn-primary ${!isDirty ? 'btn-disabled' : ''}`}
          onClick={handleSaveAll}
          title={!isDirty ? t('pendingNodesPanel.editPanel.noChanges') : t('pendingNodesPanel.editPanel.saveChanges')}
        >
          💾 {t('pendingNodesPanel.editPanel.saveAll')} {isDirty ? '✨' : '💤'}
        </button>
      </div>
    </div>
  );
};

export default PendingNodesPanel;
